import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Trash2, Route, Timer, AlertTriangle, Sparkles, Square, Copy } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, isPaidProvider, isPaidRoute } from "@/lib/api";
import { streamSSE } from "@/lib/sse";
import { FreeBadge } from "@/components/Common";

const copyText = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Kopiert"), () => toast.error("Kopieren nicht möglich"));

const Bubble = ({ m, i }) => {
  if (m.role === "user") return <div className="ml-auto max-w-[80%] rounded-2xl rounded-br-sm bg-blue-600/80 px-4 py-3 text-sm text-white whitespace-pre-wrap" data-testid={`chat-msg-user-${i}`}>{m.content}</div>;
  if (m.error) return (
    <div className="max-w-[85%] rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid={`chat-msg-error-${i}`}>
      <div className="mb-1 flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" />{m.code || "Fehler"}</div>{m.error}
      {m.partial && <div className="mt-2 whitespace-pre-wrap text-xs text-slate-300">Bis zum Fehler empfangen: {m.partial}</div>}
      {m.attempts?.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-red-300/80">{m.attempts.map((a, k) => <li key={k}>{a.provider}: {a.code}</li>)}</ul>}
    </div>
  );
  return (
    <div className="group max-w-[85%] rounded-2xl rounded-bl-sm border border-white/5 bg-white/[0.03] px-4 py-3" data-testid={`chat-msg-assistant-${i}`}>
      <div className="whitespace-pre-wrap text-sm text-slate-100">{m.content}{m.streaming && <span className="ml-0.5 inline-block h-4 w-2 animate-pulse bg-violet-400 align-middle" data-testid="chat-stream-cursor" />}</div>
      {m.aborted && <div className="mt-2 text-xs text-amber-300" data-testid={`chat-msg-aborted-${i}`}>Generierung abgebrochen</div>}
      {m.meta?.route && (
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-white/5 pt-2 text-[11px] text-slate-400" data-testid={`chat-msg-route-${i}`}>
          <span className="inline-flex items-center gap-1"><Route className="h-3 w-3" />{m.meta.route.provider_name} · {m.meta.route.model}</span>
          <FreeBadge free={m.meta.route.free} />
          <span data-testid={`chat-msg-streamed-${i}`}>{m.meta.streamed ? (m.streaming ? "streamt…" : `gestreamt · ${m.meta.chunks ?? 0} Chunks`) : "kein Streaming (normale Antwort)"}</span>
          {m.meta.duration_ms != null && <span className="inline-flex items-center gap-1"><Timer className="h-3 w-3" />{m.meta.duration_ms} ms</span>}
          {m.meta.fallback_used && <span className="text-amber-300">Fallback genutzt</span>}
          <span className="font-mono text-slate-500">{m.meta.request_id}</span>
          {!m.streaming && m.content && <button onClick={() => copyText(m.content)} data-testid={`chat-msg-copy-${i}`} className="ml-auto text-slate-400 hover:text-white"><Copy className="h-3.5 w-3.5" /></button>}
        </div>
      )}
    </div>
  );
};

export default function Playground() {
  const [catalog, setCatalog] = useState([]);
  const [creds, setCreds] = useState([]);
  const [preferFree, setPreferFree] = useState(true);
  const [fallback, setFallback] = useState(true);
  const [pid, setPid] = useState("auto");
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("");
  const [confirmPaid, setConfirmPaid] = useState(false);
  const [system, setSystem] = useState("Du bist ein hilfreicher KI-Assistent. Antworte auf Deutsch.");
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get("/catalog"), api.get("/credentials")]).then(([c, k]) => { setCatalog(c.data.providers); setCreds(k.data); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  const configured = useMemo(() => catalog.filter((p) => creds.some((c) => c.provider_id === p.id)), [catalog, creds]);
  const provider = catalog.find((p) => p.id === pid);
  useEffect(() => {
    setModel(""); setModels([]); setConfirmPaid(false);
    if (pid === "auto") return;
    api.get(`/providers/${pid}/models`).then((r) => setModels(r.data.models)).catch((e) => toast.error(errMsg(e)));
  }, [pid]);
  const shownModels = preferFree ? models.filter((m) => m.free) : models;
  const paid = isPaidRoute(provider, model);

  const send = async () => {
    if (!input.trim() || busy) return;
    const history = [...msgs.filter((m) => !m.error && m.content), { role: "user", content: input.trim() }];
    setMsgs([...msgs, { role: "user", content: input.trim() }, { role: "assistant", content: "", streaming: true }]);
    setInput(""); setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const update = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    const body = {
      model: pid === "auto" ? "auto" : `${pid}:${model}`,
      messages: [...(system.trim() ? [{ role: "system", content: system.trim() }] : []), ...history.map(({ role, content }) => ({ role, content }))],
      prefer_free: preferFree, allow_fallback: fallback, allow_paid: paid && confirmPaid,
    };
    try {
      await api.get("/auth/me"); // refreshes the session cookie if needed before streaming
      for await (const ev of streamSSE("/gateway/stream", body, ctrl.signal)) {
        if (ev.type === "meta") update((x) => ({ ...x, meta: ev }));
        else if (ev.type === "chunk") update((x) => ({ ...x, content: x.content + ev.content }));
        else if (ev.type === "done") update((x) => ({ ...x, streaming: false, meta: { ...x.meta, duration_ms: ev.duration_ms, chunks: ev.chunks } }));
        else if (ev.type === "error") update((x) => ({ role: "assistant", error: ev.message, code: ev.code, attempts: ev.attempts, partial: x.content }));
      }
    } catch (e) {
      if (e.name === "AbortError") update((x) => ({ ...x, streaming: false, aborted: true }));
      else update(() => ({ role: "assistant", error: e.message, code: e.code, attempts: e.attempts }));
    } finally {
      update((x) => ({ ...x, streaming: false }));
      abortRef.current = null;
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[340px_1fr]" data-testid="playground-page">
      <aside className="glass h-fit space-y-5 p-6">
        <h1 className="text-2xl font-bold text-white">AI Playground</h1>
        <div className="flex items-center justify-between rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
          <div><div className="text-sm font-semibold text-emerald-200">Kostenlose Modelle bevorzugen</div><div className="text-xs text-slate-400">Nur freie & lokale Routen anzeigen</div></div>
          <Switch checked={preferFree} onCheckedChange={(v) => { setPreferFree(v); if (v && isPaidProvider(provider)) setPid("auto"); }} data-testid="prefer-free-switch" />
        </div>
        <div>
          <label className="label">Provider</label>
          <select data-testid="playground-provider-select" className="field mt-1.5" value={pid} onChange={(e) => setPid(e.target.value)}>
            <option value="auto">Auto – beste kostenlose Route</option>
            {configured.map((p) => <option key={p.id} value={p.id} disabled={preferFree && isPaidProvider(p)}>{p.name}</option>)}
          </select>
          {configured.length === 0 && <p className="mt-2 text-xs text-amber-300" data-testid="playground-no-providers">Noch kein Provider eingerichtet. <Link to="/app/catalog" className="underline">Jetzt einrichten</Link></p>}
        </div>
        {pid !== "auto" && (
          <div>
            <label className="label">Modell</label>
            <select data-testid="playground-model-select" className="field mt-1.5" value={model} onChange={(e) => { setModel(e.target.value); setConfirmPaid(false); }}>
              <option value="">Standardmodell des Providers</option>
              {shownModels.map((m) => <option key={m.id} value={m.id}>{m.id}{m.free ? "" : " · kostenpflichtig"}</option>)}
            </select>
            <p className="mt-1 text-xs text-slate-500">{shownModels.length} Modelle {preferFree ? "(nur kostenlose)" : ""}</p>
          </div>
        )}
        {paid && (
          <label className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200" data-testid="paid-route-warning">
            <input type="checkbox" checked={confirmPaid} onChange={(e) => setConfirmPaid(e.target.checked)} data-testid="confirm-paid-checkbox" className="mt-0.5 accent-red-500" />
            Diese Route ist kostenpflichtig und belastet dein Provider-Konto. Ich bestätige die Nutzung.
          </label>
        )}
        <div className="flex items-center justify-between">
          <div><div className="text-sm text-slate-200">Fallback erlauben</div><div className="text-xs text-slate-500">Nur auf kostenlose/lokale Routen</div></div>
          <Switch checked={fallback} onCheckedChange={setFallback} data-testid="fallback-switch" />
        </div>
        <div><label className="label">System-Prompt</label><textarea data-testid="playground-system-input" rows={3} className="field mt-1.5" value={system} onChange={(e) => setSystem(e.target.value)} /></div>
        <p className="text-[11px] text-slate-500">Prompts werden nicht gespeichert. Anfragen an Cloud-Provider verlassen den Hub.</p>
      </aside>
      <section className="glass flex min-h-[70vh] flex-col">
        <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-300"><Sparkles className="h-4 w-4 text-violet-400" />{pid === "auto" ? "Auto-Routing (Free-First)" : `${provider?.name} · ${model || "Standardmodell"}`}</div>
          <button onClick={() => setMsgs([])} data-testid="playground-clear-button" className="btn-ghost inline-flex items-center gap-1 px-3 py-1.5 text-xs text-slate-300"><Trash2 className="h-3.5 w-3.5" />Leeren</button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-6" data-testid="chat-messages">
          {msgs.length === 0 && <div className="text-sm text-slate-500" data-testid="chat-empty">Schreibe eine Nachricht, um zu starten. Die genutzte Route wird unter jeder Antwort angezeigt.</div>}
          {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
          {busy && <div className="text-xs text-slate-400" data-testid="chat-loading">Generierung läuft (Live-Streaming)…</div>}
        </div>
        <div className="border-t border-white/5 p-4">
          <div className="flex gap-3">
            <textarea data-testid="chat-input" rows={2} className="field flex-1 resize-none" placeholder="Hier deine Nachricht eingeben…" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
            {busy ? (
              <button onClick={() => abortRef.current?.abort()} data-testid="chat-abort-button" className="btn-ghost inline-flex items-center gap-2 border-red-500/50 px-5 text-red-200"><Square className="h-4 w-4" />Abbrechen</button>
            ) : (
              <button onClick={send} disabled={!input.trim() || (paid && !confirmPaid)} data-testid="chat-send-button" className="btn-grad inline-flex items-center gap-2 px-5"><Send className="h-4 w-4" />Senden</button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
